import { FieldValues, useController, UseControllerProps } from "react-hook-form"
import { WaveInputs } from "../../../pages/PortfolioManagement/Trading/types/form"
import { DecimalInputContainer, StyledDecimalInput } from "./DecimalInput.styled"

interface DecimalInputProps<T extends FieldValues> extends UseControllerProps<T> {
  /**
   * Optional placeholder text
   */
  placeholder?: string
  /**
   * Minimum value allowed
   */
  min?: number
  /**
   * Maximum value allowed
   */
  max?: number
}

/**
 * Generic DecimalInput component for React Hook Form.
 */
export const DecimalInput = <T extends FieldValues>({
  placeholder,
  min,
  max,
  ...controllerProps
}: DecimalInputProps<T>) => {
  const {
    field: { onChange, value },
  } = useController(controllerProps)

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "ArrowUp" || event.key === "ArrowDown") {
      event.preventDefault()
      const currentValue = parseFloat(value) || 0
      // Calculate 1% of current value
      const onePercent = currentValue * 0.01
      const step = event.key === "ArrowUp" ? onePercent : -onePercent
      // Round to 2 decimal places
      const newValue = Math.round((currentValue + step) * 100) / 100

      // Respect min/max bounds if provided
      if (min !== undefined && newValue < min) return
      if (max !== undefined && newValue > max) return

      onChange(newValue)
    }
  }

  const handleBlur = (event: React.FocusEvent<HTMLInputElement>) => {
    const inputValue = event.target.value
    if (inputValue !== "" && !isNaN(parseFloat(inputValue))) {
      // Format to 2 decimal places when losing focus
      const formattedValue = parseFloat(inputValue).toFixed(2)
      onChange(parseFloat(formattedValue))
    }
  }

  return (
    <DecimalInputContainer alignItems="center">
      <StyledDecimalInput
        type="number"
        placeholder={placeholder}
        min={min}
        max={max}
        step="any"
        onKeyDown={handleKeyDown}
        onBlur={handleBlur}
        value={value}
        onChange={e => {
          const value = e.target.value
          onChange(value === "" ? "" : parseFloat(value))
        }}
      />
    </DecimalInputContainer>
  )
}
